import classnames from 'classnames';
import collect from 'collect.js';
import { KeyboardEvent, ReactElement, useRef } from 'react';

interface Item<T> {
  label: ReactElement | string;
  value: T;
  icon?: ReactElement;
  className?: string;
  activeClassName?: string;
}

interface Props {
  items: Item<any>[];
  value: any;
  className?: string;
  onClick?: (value: any) => void;
}

/** Arrow keys move focus between tabs (wrapping), Home and End to the ends; Enter and Space choose. */
const FOCUS_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

const TabBar = (props: Props): ReactElement => {
  const { items, value, onClick, className } = props;

  const _items = collect(items).whereNotNull('value').toArray();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Roving tabindex: one Tab stop for the whole bar, on the chosen tab (the first when none is).
  const activeIndex = Math.max(
    _items.findIndex((item: Item<any>) => item.value === value),
    0,
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, item: Item<any>, index: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(item.value);

      return;
    }

    if (!FOCUS_KEYS.includes(event.key)) {
      return;
    }

    event.preventDefault();

    const last = _items.length - 1;
    const next = {
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowRight: index === last ? 0 : index + 1,
      Home: 0,
      End: last,
    }[event.key];

    tabRefs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      className={classnames('grid grid-cols-2 rounded-full bg-gray-500 dark:bg-black-600 p-xs', className)}
      style={{
        gridTemplateColumns: `repeat(${_items?.length}, minmax(0, 1fr))`,
      }}
    >
      {_items.map((item: Item<any>, index: number) => (
        <div
          role="tab"
          ref={(element) => {
            tabRefs.current[index] = element;
          }}
          tabIndex={index === activeIndex ? 0 : -1}
          aria-selected={value === item.value}
          className={classnames(
            'px-2xl py-sm justify-center items-center flex rounded-full cursor-pointer z-10',
            item.className,
            {
              'border-[1px] border-brand-primary bg-white dark:bg-black': value === item.value,
              [item?.activeClassName]: value === item.value,
            },
          )}
          onClick={() => onClick(item.value)}
          onKeyDown={(event) => onKeyDown(event, item, index)}
          key={`tab-${item.label}-${index}`}
        >
          {Boolean(item.icon) && <span className="my-auto mr-xs">{item.icon}</span>}
          <h6
            className={classnames('my-auto text-brand-primary shrink-0', {
              '!text-muted-foreground': value !== item.value,
            })}
          >
            {item.label}
          </h6>
        </div>
      ))}
    </div>
  );
};

export default TabBar;
